import { Fragment, type ReactNode } from 'react';
import {
  MarketContentPanel,
  MarketEmptyPanel,
  MarketLoadingPanel,
  MarketPanelHeader,
} from '~/features/shared/market-content';
import { OpportunityFilters } from './OpportunityFilters';

type OpportunityFilter = 'all' | 'active' | 'accepted';

type FilterTab = {
  label: string;
  value: OpportunityFilter;
};

export type OpportunitiesEmptyState = {
  title: string;
  description?: string;
  action?: ReactNode;
};

type Props<TOpportunity extends { responseId: number }> = {
  opportunities: TOpportunity[];
  selectedResponseId: number | undefined;
  filter: OpportunityFilter;
  /** [MCV-1850] Declined and withdrawn responses are hidden unless this is set. */
  includeDeclined?: boolean;
  loading: boolean;
  title: string;
  subtitle?: ReactNode;
  emptyState?: OpportunitiesEmptyState;
  onSelect: (responseId: number) => void;
  onFilter: (filter: OpportunityFilter) => void;
  onIncludeDeclined?: (includeDeclined: boolean) => void;
  /** [MCV-1889] Below 1360px the filters collapse into a "Filters (N)" drawer. */
  responsiveFilters?: boolean;
  renderOpportunity: (
    opportunity: TOpportunity,
    isSelected: boolean,
    onSelect: (responseId: number) => void
  ) => ReactNode;
};

const FILTER_TABS: FilterTab[] = [
  { label: 'All', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Accepted', value: 'accepted' },
];

const DEFAULT_EMPTY_STATE: OpportunitiesEmptyState = {
  title: 'No opportunities found',
  description: '',
};

export const OpportunitiesSidebar = <TOpportunity extends { responseId: number }>({
  opportunities,
  selectedResponseId,
  filter,
  includeDeclined = false,
  loading,
  title,
  subtitle,
  emptyState = DEFAULT_EMPTY_STATE,
  onSelect,
  onFilter,
  onIncludeDeclined,
  responsiveFilters = false,
  renderOpportunity,
}: Props<TOpportunity>) => {
  return (
    <MarketContentPanel
      className="h-full"
      bodyClassName="flex min-h-0 flex-1 flex-col p-2"
      header={
        <>
          <MarketPanelHeader title={title} subtitle={subtitle} />
          <OpportunityFilters
            options={FILTER_TABS.map((tab) => ({ ...tab, cyTag: `filter-tab-${tab.value}` }))}
            filter={filter}
            onFilter={onFilter}
            includeDeclined={includeDeclined}
            onIncludeDeclined={onIncludeDeclined}
            responsive={responsiveFilters}
          />
        </>
      }
      variant="scroll"
    >
      {loading ? (
        <MarketLoadingPanel />
      ) : opportunities.length === 0 ? (
        <MarketEmptyPanel
          title={emptyState.title}
          description={emptyState.description}
          action={emptyState.action}
          className="min-[1360px]:-translate-y-12"
        />
      ) : (
        opportunities.map((opportunity) => (
          <Fragment key={opportunity.responseId}>
            {renderOpportunity(
              opportunity,
              opportunity.responseId === selectedResponseId,
              onSelect
            )}
          </Fragment>
        ))
      )}
    </MarketContentPanel>
  );
};
